// My Shifts read model - public.my_shifts (security_invoker; own rows only via auth.uid()).
// Mirrors the Codex-hardened console query exactly: explicit column allowlist (no wildcard, no
// user ids / tokens / coordinates / notes), scheduled+active assignments only, upcoming window,
// soonest first. Read-only - responses go through the RPC in ./submit-response, never the table.

export const MY_SHIFTS_COLUMNS =
  "route_assignment_id, route_name, site_name, slot_date, starts_at, ends_at, assignment_status, response_status, decline_reason, responded_at, required_credential_types";

export interface MyShiftRow {
  routeAssignmentId: string;
  routeName: string | null;
  siteName: string | null;
  slotDate: string | null;
  startsAt: string | null;
  endsAt: string | null;
  assignmentStatus: string;
  /** 'none' | 'acknowledged' | 'declined' (view coalesces absent responses to 'none'). */
  responseStatus: string;
  declineReason: string | null;
  respondedAt: string | null;
  requiredCredentialTypes: string[];
}

export interface MyShiftsQueryClient {
  from(table: string): {
    select(columns: string): {
      eq(
        column: string,
        value: string,
      ): {
        in(
          column: string,
          values: string[],
        ): {
          gte(
            column: string,
            value: string,
          ): {
            order(
              column: string,
              options: { ascending: boolean },
            ): {
              limit(count: number): Promise<{ data: unknown; error: { message: string } | null }>;
            };
          };
        };
      };
    };
  };
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

export function mapMyShiftRow(raw: Record<string, unknown>): MyShiftRow {
  return {
    routeAssignmentId: asString(raw.route_assignment_id) ?? "",
    routeName: asString(raw.route_name),
    siteName: asString(raw.site_name),
    slotDate: asString(raw.slot_date),
    startsAt: asString(raw.starts_at),
    endsAt: asString(raw.ends_at),
    assignmentStatus: asString(raw.assignment_status) ?? "unknown",
    responseStatus: asString(raw.response_status) ?? "none",
    declineReason: asString(raw.decline_reason),
    respondedAt: asString(raw.responded_at),
    requiredCredentialTypes: Array.isArray(raw.required_credential_types)
      ? raw.required_credential_types.filter((item): item is string => typeof item === "string")
      : [],
  };
}

export const MY_SHIFTS_ROW_LIMIT = 50;

export async function fetchMyShifts(
  client: MyShiftsQueryClient,
  accountId: string,
  fromIso: string,
  limit: number = MY_SHIFTS_ROW_LIMIT,
): Promise<MyShiftRow[]> {
  const { data, error } = await client
    .from("my_shifts")
    .select(MY_SHIFTS_COLUMNS)
    .eq("account_id", accountId)
    .in("assignment_status", ["scheduled", "active"])
    .gte("ends_at", fromIso)
    .order("starts_at", { ascending: true })
    .limit(limit);
  if (error) {
    throw new Error("Could not load your shifts.");
  }
  const rows = Array.isArray(data) ? (data as unknown[]) : [];
  return rows
    .filter((row): row is Record<string, unknown> => typeof row === "object" && row !== null)
    .map(mapMyShiftRow)
    .filter((row) => row.routeAssignmentId !== "");
}
